import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api';
import { showToast } from '../utils/toaster';
import { getRuntimeVariablesForCategories } from '../utils/runtimeVariables';
import EmailIntegrationForm from './integration-forms/EmailIntegrationForm';
import SlackIntegrationForm from './integration-forms/SlackIntegrationForm';
import WebhookIntegrationForm from './integration-forms/WebhookIntegrationForm';
import StreamIntegrationForm from './integration-forms/StreamIntegrationForm';
import RuntimeVariablesSidebar from './RuntimeVariablesSidebar';

interface IntegrationFieldMetadata {
  name: string;
  label: string;
  type: string;
  required: boolean;
}

interface IntegrationType {
  enum_value: string;
  name: string;
  description: string;
  metadata: {
    fields?: IntegrationFieldMetadata[];
    [key: string]: any;
  };
}

interface IntegrationCategory {
  enum_value: string;
  name: string;
  description: string;
  metadata: Record<string, any>;
}

interface IntegrationConfig {
  types: IntegrationType[];
  categories: IntegrationCategory[];
}

interface EmailConfiguration {
  smtp_host: string;
  smtp_port: number;
  smtp_username: string;
  smtp_password: string;
  from: string;
  to: string[];
  use_tls: boolean;
  use_starttls: boolean;
}

interface EmailContent {
  subject: string;
  body: string;
  format?: 'plain' | 'html';
}

interface SlackConfiguration {
  webhook_url: string;
}

interface SlackContent {
  text: string;
  bot_name?: string;
  icon_emoji?: string;
  channel?: string;
}

interface WebhookConfiguration {
  url: string;
  method?: string;
  signing_secret?: string;
  headers?: Record<string, string>;
}

interface WebhookContent {
  [key: string]: any;
}

interface StreamConfiguration {
  platform: string;
  topic: string;
  brokers?: string;
  region?: string;
  redis_url?: string;
  auth_type?: string;
  sasl_username?: string;
  sasl_password?: string;
  access_key?: string;
  secret_key?: string;
}

interface StreamContent {
  [key: string]: any;
}

const AddIntegrationWizardPage: React.FC = () => {
  const navigate = useNavigate();
  // console.log('[AddIntegrationWizardPage] Component rendering');
  const [integrationConfig, setIntegrationConfig] = useState<IntegrationConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('email');
  const [category, setCategory] = useState('general');
  const [configuration, setConfiguration] = useState('{}');
  const [content, setContent] = useState('{}');
  
  // Email-specific state
  const [emailConfiguration, setEmailConfiguration] = useState<EmailConfiguration>({
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    from: '',
    to: [],
    use_tls: false,
    use_starttls: true,
  });
  const [emailContent, setEmailContent] = useState<EmailContent>({
    subject: '',
    body: '',
    format: 'plain',
  });
  
  // Slack-specific state
  const [slackConfiguration, setSlackConfiguration] = useState<SlackConfiguration>({
    webhook_url: '',
  });
  const [slackContent, setSlackContent] = useState<SlackContent>({
    text: '',
    bot_name: '',
    icon_emoji: '',
    channel: '',
  });
  
  // Webhook-specific state
  const [webhookConfiguration, setWebhookConfiguration] = useState<WebhookConfiguration>({
    url: '',
    method: 'POST',
    signing_secret: '',
  });
  const [webhookContent, setWebhookContent] = useState<WebhookContent>({});
  
  // Stream-specific state
  const [streamConfiguration, setStreamConfiguration] = useState<StreamConfiguration>({
    platform: 'kafka',
    topic: '',
  });
  const [streamContent, setStreamContent] = useState<StreamContent>({});
  
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasMissingVariables, setHasMissingVariables] = useState(false);
  const [validRuntimeVars, setValidRuntimeVars] = useState<Set<string>>(new Set());

  // console.log('[AddIntegrationWizardPage] Component rendering, streamConfiguration:', JSON.stringify(streamConfiguration));
  // console.log('[AddIntegrationWizardPage] hasMissingVariables:', hasMissingVariables, 'validRuntimeVars size:', validRuntimeVars.size);

  // Fetch integration configuration from backend
  useEffect(() => {
    const fetchIntegrationConfig = async () => {
      try {
        // console.log('[AddIntegrationWizardPage] Fetching integration config from /integrations/config');
        const response = await apiClient.get('/integrations/config');
        // console.log('[AddIntegrationWizardPage] Integration config response:', response);
        // console.log('[AddIntegrationWizardPage] Integration config data:', response.data);
        setIntegrationConfig(response.data);
        
        // Set default type to first available type
        if (response.data.types && response.data.types.length > 0) {
          // console.log('[AddIntegrationWizardPage] Setting default type to:', response.data.types[0].enum_value);
          setType(response.data.types[0].enum_value);
        }
        
        // Set default category to first available category
        if (response.data.categories && response.data.categories.length > 0) {
          // console.log('[AddIntegrationWizardPage] Setting default category to:', response.data.categories[0].enum_value);
          setCategory(response.data.categories[0].enum_value);
        }
        
        // console.log('[AddIntegrationWizardPage] Integration config setup complete');
      } catch (err) {
        console.error('[AddIntegrationWizardPage] Failed to fetch integration config:', err);
        console.error('[AddIntegrationWizardPage] Error details:', {
          message: (err as any).message,
          response: (err as any).response,
          data: (err as any).response?.data
        });
        showToast('error', 'Failed to load integration configuration');
      } finally {
        setConfigLoading(false);
      }
    };

    fetchIntegrationConfig();
  }, []);

  // Fetch valid runtime variables when category changes
  useEffect(() => {
    const fetchValidRuntimeVars = async () => {
      try {
        const vars = await getRuntimeVariablesForCategories(['general', category || 'general']);
        const validNames = new Set(Object.keys(vars));
        setValidRuntimeVars(validNames);
      } catch (err) {
        console.error('[AddIntegrationWizardPage] Failed to fetch valid runtime variables:', err);
      }
    };

    fetchValidRuntimeVars();
  }, [category]);

  // Stable callback for stream configuration updates
  const handleStreamConfigurationChange = useCallback((config: StreamConfiguration) => {
    // console.log('[AddIntegrationWizardPage] handleStreamConfigurationChange called with:', JSON.stringify(config, null, 2));
    setStreamConfiguration(config);
  }, []);

  // Stable callback for stream content updates
  const handleStreamContentChange = useCallback((content: StreamContent) => {
    setStreamContent(content);
  }, []);

  // Stable callback for missing variables change
  const handleMissingVariablesChange = useCallback((count: number) => {
    setHasMissingVariables(count > 0);
  }, []);

  // Keyboard shortcut for saving (Cmd+S / Ctrl+S)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        if (!isSubmitting) {
          const form = document.querySelector('form');
          if (form) {
            form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSubmitting]);

  const getConfigurationPlaceholder = (notifierType: string) => {
    switch (notifierType) {
      case 'email':
        return `{
  "smtp_host": "smtp.gmail.com",
  "smtp_port": 587,
  "smtp_username": "user@gmail.com",
  "smtp_password": "app-password",
  "from": "gateway@company.com",
  "to": ["admin@company.com"],
  "use_tls": false,
  "use_starttls": true
}`;
      case 'slack':
        return `{
  "webhook_url": "https://hooks.slack.com/services/YOUR/WEBHOOK/URL"
}`;
      case 'webhook':
        return `{
  "url": "https://your-server.com/webhook",
  "method": "POST",
  "headers": {
    "Authorization": "Bearer YOUR_TOKEN"
  }
}`;
      case 'stream':
        return `{
  "platform": "kafka",
  "topic": "trust-gateway-events",
  "brokers": "localhost:9092,localhost:9093",
  "auth_type": "sasl_plain",
  "username": "your-username",
  "password": "your-password",
  "partition_key": "\${EVENT_TYPE}"
}`;
      default:
        return '{"key": "value"}';
    }
  };

  const getContentPlaceholder = (notifierType: string) => {
    switch (notifierType) {
      case 'email':
        return `{
  "subject": "Notification from \${CP_NAME}",
  "body": "Message received at \${TIMESTAMP}\\n\\nMessage ID: \${MESSAGE_ID}"
}`;
      case 'slack':
        return `{
  "text": "🔔 Notification from *\${CP_NAME}*\\n\\nMessage ID: \`\${MESSAGE_ID}\`\\nTime: \${TIMESTAMP}",
  "bot_name": "Trust Gateway",
  "icon_emoji": ":robot_face:",
  "channel": "#notifications"
}`;
      case 'webhook':
        return `{
  "connection_point": "\${CP_NAME}",
  "message_id": "\${MESSAGE_ID}",
  "timestamp": "\${TIMESTAMP}",
  "event": "notification"
}`;
      case 'stream':
        return `{
  "event_type": "\${EVENT_TYPE}",
  "entity_id": "\${ENTITY_ID}",
  "entity_type": "\${ENTITY_TYPE}",
  "timestamp": "\${TIMESTAMP}",
  "new_state": "\${NEW_STATE}",
  "old_state": "\${OLD_STATE}"
}`;
      default:
        return '{}';
    }
  };

  const getDefaultConfiguration = (notifierType: string) => {
    switch (notifierType) {
      case 'email':
        return `{
  "smtp_host": "",
  "smtp_port": 587,
  "smtp_username": "",
  "smtp_password": "",
  "from": "",
  "to": [],
  "use_tls": false,
  "use_starttls": true
}`;
      case 'slack':
        return `{
  "webhook_url": ""
}`;
      case 'webhook':
        return `{
  "url": "",
  "method": "POST"
}`;
      case 'stream':
        return `{
  "platform": "kafka",
  "topic": "",
  "brokers": "",
  "auth_type": "none"
}`;
      default:
        return '{}';
    }
  };

  const getDefaultContent = (notifierType: string) => {
    switch (notifierType) {
      case 'email':
        return `{
  "subject": "",
  "body": ""
}`;
      case 'slack':
        return `{
  "text": ""
}`;
      case 'webhook':
        return `{}`;
      case 'stream':
        return `{
  "event_type": "\${EVENT_TYPE}",
  "timestamp": "\${TIMESTAMP}"
}`;
      default:
        return '{}';
    }
  };

  const handleTypeChange = (newType: string) => {
    setType(newType);
    // Only update if empty or default {}
    if (configuration.trim() === '{}' || configuration.trim() === '') {
      setConfiguration(getDefaultConfiguration(newType));
    }
    if (content.trim() === '{}' || content.trim() === '') {
      setContent(getDefaultContent(newType));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!name.trim()) {
      setError('Integration name is required');
      return;
    }

    let configObj: any;
    let contentObj: any;

    // Get configuration and content based on type
    if (type === 'email') {
      // Validate email fields
      if (!emailConfiguration.smtp_host || !emailConfiguration.smtp_username || 
          !emailConfiguration.smtp_password || !emailConfiguration.from || 
          emailConfiguration.to.length === 0) {
        setError('Please fill in all required email configuration fields');
        return;
      }
      if (!emailContent.subject || !emailContent.body) {
        setError('Please fill in email subject and body');
        return;
      }
      configObj = emailConfiguration;
      contentObj = emailContent;
    } else if (type === 'slack') {
      // Validate slack fields
      if (!slackConfiguration.webhook_url) {
        setError('Please provide a Slack webhook URL');
        return;
      }
      if (!slackContent.text) {
        setError('Please provide message text');
        return;
      }
      configObj = slackConfiguration;
      // Filter out empty optional fields
      contentObj = {
        text: slackContent.text,
        ...(slackContent.bot_name && { bot_name: slackContent.bot_name }),
        ...(slackContent.icon_emoji && { icon_emoji: slackContent.icon_emoji }),
        ...(slackContent.channel && { channel: slackContent.channel }),
      };
    } else if (type === 'webhook') {
      // Validate webhook fields
      if (!webhookConfiguration.url) {
        setError('Please provide a webhook URL');
        return;
      }
      // Filter out empty optional fields
      configObj = {
        url: webhookConfiguration.url,
        method: webhookConfiguration.method || 'POST',
        ...(webhookConfiguration.signing_secret && { signing_secret: webhookConfiguration.signing_secret }),
        ...(webhookConfiguration.headers && Object.keys(webhookConfiguration.headers).length > 0 && { headers: webhookConfiguration.headers }),
      };
      contentObj = webhookContent;
    } else if (type === 'stream') {
      // Validate stream fields
      // console.log('[AddIntegrationWizardPage] Stream validation - streamConfiguration:', JSON.stringify(streamConfiguration, null, 2));
      if (!streamConfiguration.platform) {
        setError('Please select a streaming platform');
        return;
      }
      if (!streamConfiguration.topic || streamConfiguration.topic.trim() === '') {
        setError('Please provide a topic/stream name');
        return;
      }
      // Platform-specific validation
      if ((streamConfiguration.platform === 'kafka' || streamConfiguration.platform === 'pulsar') && !streamConfiguration.brokers) {
        setError('Please provide broker URLs for Kafka/Pulsar');
        return;
      }
      if (streamConfiguration.platform === 'kinesis' && !streamConfiguration.region) {
        setError('Please provide AWS region for Kinesis');
        return;
      }
      if (streamConfiguration.platform === 'redis' && !streamConfiguration.redis_url) {
        setError('Please provide Redis URL');
        return;
      }
      
      configObj = streamConfiguration;
      contentObj = streamContent;
    } else {
      // For other types, validate JSON
      try {
        configObj = JSON.parse(configuration);
      } catch (err) {
        setError('Configuration must be valid JSON');
        return;
      }

      try {
        contentObj = JSON.parse(content);
      } catch (err) {
        setError('Content must be valid JSON');
        return;
      }
    }

    try {
      setIsSubmitting(true);
      
      const payload = {
        name: name.trim(),
        description: description.trim(),
        type,
        category: category || 'general',
        configuration: configObj,
        content: contentObj,
        status: 'active',
      };

      const response = await apiClient.post('/integrations', payload);
      showToast('success', `Integration "${name}" has been successfully created.`, { autoRemove: true, duration: 2000 });
      
      // Navigate to the integration edit page or integrations page
      if (response.data && response.data.id) {
        navigate(`/integrations/integrations/${response.data.id}`, { replace: true });
      } else {
        navigate('/integrations', { replace: true });
      }
    } catch (err: any) {
      console.error('Failed to create integration:', err);
      setError(err.response?.data?.message || err.message || 'Failed to create integration');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/integrations');
  };

  return (
    <div className="container-fluid">
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <div>
          <h1 className="h3 mb-0 text-gray-800">
            <i className="fas fa-bell mr-2"></i>
            Add integration
          </h1>
          <p className="text-muted mt-2">
            Create a new integration to send notifications about gateway events
          </p>
        </div>
        <button
          className="btn btn-sm btn-secondary"
          onClick={handleCancel}
          disabled={isSubmitting}
        >
          <i className="fas fa-times mr-1"></i>
          Cancel
        </button>
      </div>

      <div className="row">
        <div className="col-lg-8">
          <div className="card shadow">
            <div className="card-header py-3">
              <h6 className="m-0 font-weight-bold text-primary">
                <i className="fas fa-info-circle mr-2"></i>
                Integration Details
              </h6>
            </div>
            <div className="card-body">
              {error && (
                <div className="alert alert-danger" role="alert">
                  <i className="fas fa-exclamation-triangle mr-2"></i>
                  {error}
                </div>
              )}

              {configLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  <p className="text-muted mt-2">Loading integration configuration...</p>
                </div>
              ) : !integrationConfig ? (
                <div className="alert alert-warning" role="alert">
                  <i className="fas fa-exclamation-triangle mr-2"></i>
                  Failed to load integration configuration
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label htmlFor="type" className="form-label">
                      Integration Type *
                    </label>
                    <select
                      id="type"
                      className="form-control dropdown-styling"
                      value={type}
                      onChange={(e) => handleTypeChange(e.target.value)}
                      disabled={isSubmitting}
                      required
                    >
                      {integrationConfig.types.map((t) => (
                        <option key={t.enum_value} value={t.enum_value} title={t.description}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                    <small className="form-text text-muted">
                      <i className="fas fa-info-circle mr-1"></i>
                      {integrationConfig.types.find(t => t.enum_value === type)?.description || 'How notifications will be sent'}
                    </small>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="category" className="form-label">
                      Category *
                    </label>
                    <select
                      id="category"
                      className="form-control dropdown-styling"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      disabled={isSubmitting}
                      required
                    >
                      {integrationConfig.categories.map((c) => (
                        <option key={c.enum_value} value={c.enum_value} title={c.description}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                    <small className="form-text text-muted">
                      <i className="fas fa-info-circle mr-1"></i>
                      {integrationConfig.categories.find(c => c.enum_value === category)?.description || 'Context determines which runtime variables are available'}
                    </small>
                  </div>

                <div className="mb-3">
                  <label htmlFor="name" className="form-label">
                    Integration Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    className="form-control"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., Email integration"
                    disabled={isSubmitting}
                    required
                  />
                  <small className="form-text text-muted">
                    A friendly name to identify this integration
                  </small>
                </div>

                <div className="mb-3">
                  <label htmlFor="description" className="form-label">
                    Description
                  </label>
                  <input
                    id="description"
                    className="form-control"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe the purpose of this integration..."
                    disabled={isSubmitting}
                  />
                  <small className="form-text text-muted">
                    Optional description of what this integration is used for
                  </small>
                </div>

                {type === 'email' ? (
                  <EmailIntegrationForm
                    configuration={emailConfiguration}
                    content={emailContent}
                    onConfigurationChange={setEmailConfiguration}
                    onContentChange={setEmailContent}
                    disabled={isSubmitting}
                    validRuntimeVars={validRuntimeVars}
                  />
                ) : type === 'slack' ? (
                  <SlackIntegrationForm
                    configuration={slackConfiguration}
                    content={slackContent}
                    onConfigurationChange={setSlackConfiguration}
                    onContentChange={setSlackContent}
                    disabled={isSubmitting}
                    validRuntimeVars={validRuntimeVars}
                  />
                ) : type === 'webhook' ? (
                  <WebhookIntegrationForm
                    configuration={webhookConfiguration}
                    content={webhookContent}
                    onConfigurationChange={setWebhookConfiguration}
                    onContentChange={setWebhookContent}
                    disabled={isSubmitting}
                    validRuntimeVars={validRuntimeVars}
                  />
                ) : type === 'stream' ? (
                  <StreamIntegrationForm
                    configuration={streamConfiguration}
                    content={streamContent}
                    onConfigurationChange={handleStreamConfigurationChange}
                    onContentChange={handleStreamContentChange}
                    disabled={isSubmitting}
                    validRuntimeVars={validRuntimeVars}
                  />
                ) : (
                  <>
                    <div className="mb-3">
                      <label htmlFor="configuration" className="form-label">
                        Configuration (JSON) *
                      </label>
                      <textarea
                        id="configuration"
                        className="form-control font-monospace"
                        rows={10}
                        value={configuration}
                        onChange={(e) => setConfiguration(e.target.value)}
                        placeholder={getConfigurationPlaceholder(type)}
                        disabled={isSubmitting}
                        required
                      />
                      <small className="form-text text-muted">
                        <i className="fas fa-info-circle mr-1"></i>
                        Connection and authentication settings (e.g., SMTP host/port/credentials, Slack webhook URL, etc.)
                      </small>
                    </div>

                    <div className="mb-3">
                      <label htmlFor="content" className="form-label">
                        Content Template (JSON) *
                      </label>
                      <textarea
                        id="content"
                        className="form-control font-monospace"
                        rows={10}
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder={getContentPlaceholder(type)}
                        disabled={isSubmitting}
                        required
                      />
                      <small className="form-text text-muted">
                        <i className="fas fa-info-circle mr-1"></i>
                        Message content template with optional variables like {`\${CP_NAME}`}, {`\${MESSAGE_ID}`}, {`\${TIMESTAMP}`}
                      </small>
                    </div>
                  </>
                )}

                <div className="alert alert-info mb-4">
                  <i className="fas fa-info-circle mr-2"></i>
                  <strong>Integrations:</strong> Receive notifications about gateway events such as channel status changes, message failures, and system alerts.
                </div>

                <div className="d-flex justify-content-between">
                  <button 
                    type="button" 
                    className="btn btn-outline-secondary" 
                    onClick={handleCancel}
                    disabled={isSubmitting}
                  >
                    <i className="fas fa-times mr-1"></i>
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={isSubmitting || hasMissingVariables}
                    title={hasMissingVariables ? 'Cannot create: integration has missing variables' : ''}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                        Creating...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-plus mr-1"></i> Create integration
                      </>
                    )}
                  </button>
                </div>
              </form>
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-4">
          {type === 'email' ? (
            <>
              <div className="card shadow mb-4">
                <div className="card-header py-3">
                  <h6 className="m-0 font-weight-bold text-primary">
                    <i className="fas fa-lightbulb mr-2"></i>
                    Quick Setup Guide
                  </h6>
                </div>
                <div className="card-body">
                  <ol className="mb-0" style={{ fontSize: '0.9em', paddingLeft: '20px' }}>
                    <li>Choose SMTP provider (Gmail, SendGrid, etc.)</li>
                    <li>Get SMTP credentials</li>
                    <li>Configure host, port, and credentials</li>
                    <li>Set recipient email addresses</li>
                    <li>Write subject and body with template variables</li>
                    <li>Test the configuration</li>
                  </ol>
                </div>
              </div>
              
              <RuntimeVariablesSidebar 
                category={category}
                configuration={emailConfiguration}
                content={emailContent}
                onMissingVariablesChange={handleMissingVariablesChange}
              />
              
              <a 
                href="/guides/email-integration.html" 
                target="_blank" 
                className="btn btn-sm btn-outline-primary btn-block"
              >
                <i className="fas fa-book mr-2"></i>
                View Complete Guide
              </a>
            </>
          ) : type === 'slack' ? (
            <>
              <div className="card shadow mb-4">
                <div className="card-header py-3">
                  <h6 className="m-0 font-weight-bold text-primary">
                    <i className="fas fa-lightbulb mr-2"></i>
                    How to Get Webhook URL
                  </h6>
                </div>
                <div className="card-body">
                  <ol className="mb-0" style={{ fontSize: '0.9em', paddingLeft: '20px' }}>
                    <li>Go to <a href="https://api.slack.com/apps" target="_blank" rel="noopener noreferrer">api.slack.com/apps</a></li>
                    <li>Create a new app or select an existing one</li>
                    <li>Enable "Incoming Webhooks"</li>
                    <li>Click "Add New Webhook to Workspace"</li>
                    <li>Select a channel and authorize</li>
                    <li>Copy the webhook URL</li>
                  </ol>
                </div>
              </div>
              
              <RuntimeVariablesSidebar 
                category={category}
                configuration={slackConfiguration}
                content={slackContent}
                onMissingVariablesChange={handleMissingVariablesChange}
              />
              
              <a 
                href="/guides/slack-integration.html" 
                target="_blank" 
                className="btn btn-sm btn-outline-primary btn-block"
              >
                <i className="fas fa-book mr-2"></i>
                View Complete Guide
              </a>
            </>
          ) : type === 'webhook' ? (
            <>
              <div className="card shadow mb-4">
                <div className="card-header py-3">
                  <h6 className="m-0 font-weight-bold text-primary">
                    <i className="fas fa-lightbulb mr-2"></i>
                    Quick Setup Guide
                  </h6>
                </div>
                <div className="card-body">
                  <ol className="mb-0" style={{ fontSize: '0.9em', paddingLeft: '20px' }}>
                    <li>Prepare your webhook endpoint (POST/PUT/PATCH)</li>
                    <li>Generate a signing secret (recommended)</li>
                    <li>Configure webhook URL and method</li>
                    <li>Design JSON payload template with variables</li>
                    <li>Implement signature verification on your server</li>
                    <li>Test the webhook</li>
                  </ol>
                  <hr />
                  <p className="mb-2 mt-3"><small><strong>Security:</strong></small></p>
                  <p className="mb-0" style={{ fontSize: '0.85em' }}>
                    Use HMAC-SHA256 signing to verify webhook authenticity and prevent tampering. See the complete guide for implementation examples.
                  </p>
                </div>
              </div>
              
              <RuntimeVariablesSidebar 
                category={category}
                configuration={webhookConfiguration}
                content={webhookContent}
                onMissingVariablesChange={handleMissingVariablesChange}
              />
              
              <a 
                href="/guides/webhook-integration.html" 
                target="_blank" 
                className="btn btn-sm btn-outline-primary btn-block"
              >
                <i className="fas fa-book mr-2"></i>
                View Complete Guide
              </a>
            </>
          ) : type === 'stream' ? (
            <>
              <div className="card shadow mb-4">
                <div className="card-header py-3">
                  <h6 className="m-0 font-weight-bold text-primary">
                    <i className="fas fa-lightbulb mr-2"></i>
                    Quick Setup Guide
                  </h6>
                </div>
                <div className="card-body">
                  <ol className="mb-0" style={{ fontSize: '0.9em', paddingLeft: '20px' }}>
                    <li>Choose streaming platform (Kafka, Kinesis, Pulsar, Redis)</li>
                    <li>Configure platform-specific connection details</li>
                    <li>Set topic/stream name</li>
                    <li>Design JSON event template with variables</li>
                    <li>Test the stream integration</li>
                  </ol>
                  <hr />
                  <p className="mb-2 mt-3"><small><strong>Event Format:</strong></small></p>
                  <p className="mb-0" style={{ fontSize: '0.85em' }}>
                    Events are published as JSON payloads. Use template variables to include dynamic data from gateway events.
                  </p>
                </div>
              </div>
              
              <RuntimeVariablesSidebar 
                category={category}
                configuration={streamConfiguration}
                content={streamContent}
                onMissingVariablesChange={handleMissingVariablesChange}
              />
              
              <a 
                href="/guides/stream-integration.html" 
                target="_blank" 
                className="btn btn-sm btn-outline-primary btn-block"
              >
                <i className="fas fa-book mr-2"></i>
                View Complete Guide
              </a>
            </>
          ) : (
            <>
              <div className="card shadow mb-4">
                <div className="card-header py-3">
                  <h6 className="m-0 font-weight-bold text-primary">
                    <i className="fas fa-info-circle mr-2"></i>
                    Integration Info
                  </h6>
                </div>
                <div className="card-body">
                  <p className="mb-2">
                    Integrations send alerts about gateway events such as:
                  </p>
                  <ul className="mb-0">
                    <li>Channel status changes</li>
                    <li>Message delivery failures</li>
                    <li>System alerts and warnings</li>
                    <li>Configuration updates</li>
                  </ul>
                </div>
              </div>
              
              <RuntimeVariablesSidebar 
                category={category}
                configuration={
                  type === 'email' ? emailConfiguration :
                  type === 'slack' ? slackConfiguration :
                  type === 'webhook' ? webhookConfiguration :
                  type === 'stream' ? streamConfiguration :
                  undefined
                }
                content={
                  type === 'email' ? emailContent :
                  type === 'slack' ? slackContent :
                  type === 'webhook' ? webhookContent :
                  type === 'stream' ? streamContent :
                  undefined
                }
                onMissingVariablesChange={handleMissingVariablesChange}
              />
            </>
          )}
        </div>
      </div>
      <br />
    </div>
  );
};

export default AddIntegrationWizardPage;
